import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';

/**
 * IntercomEmbed Component
 * 
 * This component embeds the Intercom conversation interface directly into our application.
 * It loads the Intercom Messenger and configures it to display a specific conversation.
 * 
 * @param {Object} props
 * @param {string} props.conversationId - The ID of the Intercom conversation to display
 * @param {string} props.appId - The Intercom App ID
 * @param {string} props.accessToken - The Intercom Access Token
 * @param {Function} props.onConversationLoaded - Callback when conversation is loaded
 */
const IntercomEmbed = ({ conversationId, appId, accessToken, onConversationLoaded }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  useEffect(() => {
    // Load Intercom script
    const loadIntercom = () => {
      if (window.Intercom) {
        initializeIntercom();
        return;
      }

      // For Intercom, we need to use their standard app ID format
      // If the provided appId is in UUID format, we'll use a fallback for testing
      const intercomAppId = appId && appId.length < 20 ? appId : 'k7aglsqm';

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://widget.intercom.io/widget/' + intercomAppId;
      script.onload = initializeIntercom;
      script.onerror = () => setError('Failed to load Intercom widget');
      document.head.appendChild(script);
    };

    // Initialize Intercom with the specific conversation
    const initializeIntercom = () => {
      // For Intercom, we need to use their standard app ID format
      // If the provided appId is in UUID format, we'll use a fallback for testing
      const intercomAppId = appId && appId.length < 20 ? appId : 'k7aglsqm';

      window.Intercom('boot', {
        app_id: intercomAppId,
        api_base: 'https://api-iam.intercom.io',
        alignment: 'left',
        horizontal_padding: 0,
        vertical_padding: 0,
        custom_launcher_selector: '#intercom-custom-launcher',
        hide_default_launcher: true
      });

      // Set up authentication if we have an access token
      if (accessToken) {
        window.Intercom('update', {
          user_id: 'doctor_user',
          name: 'Healthcare Provider',
          email: 'doctor@example.com',
          created_at: Math.floor(Date.now() / 1000),
          user_hash: accessToken // In a real implementation, this would be a proper HMAC
        });
      }

      // Open the specific conversation
      if (conversationId) {
        window.Intercom('showConversation', conversationId);
      }

      // Set up event listeners
      window.Intercom('onShow', () => {
        setLoading(false);
        if (onConversationLoaded) {
          onConversationLoaded();
        }
      });

      window.Intercom('onHide', () => {
        // Handle conversation being closed
      });
    };

    // Clean up function
    const cleanupIntercom = () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };

    // Load Intercom
    loadIntercom();

    // Clean up on unmount
    return cleanupIntercom;
  }, [appId, conversationId, accessToken, onConversationLoaded]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          bgcolor: 'background.paper'
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading conversation...
          </Typography>
        </Box>
      )}

      {error && (
        <Paper sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      <Box 
        ref={containerRef}
        id="intercom-container"
        sx={{ 
          height: '100%', 
          width: '100%',
          '& iframe': {
            height: '100% !important',
            width: '100% !important',
            border: 'none !important',
            borderRadius: '8px !important'
          }
        }}
      />
      <div id="intercom-custom-launcher" style={{ display: 'none' }}></div>
    </Box>
  );
};

export default IntercomEmbed; 